import type { BibleHighlight, HighlightFamily } from '@/types/highlight';

// Pure, probe-importable helpers for the meaning-first highlight layer (Bible
// reader rethink Chunk C). No React / IDB / zustand imports (the BibleHighlight
// type is erased at runtime), so scripts/probe-highlights.mjs loads this under
// tsx with default resolution.

export function highlightId(
  bookSlug: string,
  chapter: number,
  verse: number,
): string {
  return `${bookSlug}:${chapter}:${verse}`;
}

/** Per-chapter map: verse -> live (non-tombstone) highlight. bookSlug-guarded by
 *  the caller. Mirrors bookmarkCountsByVerse's shape/role. One row per verse by
 *  construction (the deterministic id), so the last writer for a verse wins. */
export function highlightsByVerse(
  highlights: BibleHighlight[],
  bookSlug: string,
  chapter: number,
): Map<number, BibleHighlight> {
  const map = new Map<number, BibleHighlight>();
  for (const h of highlights) {
    if (h.deletedAt) continue; // defense in depth (store already filters)
    if (h.bookSlug !== bookSlug || h.chapter !== chapter) continue;
    map.set(h.verse, h); // one row per verse by construction
  }
  return map;
}

// The 6 lead families + Important / Other, in palette display order (taxonomy
// revision, operator-locked 2026-06-20).
export const HL_FAMILY_KEYS: HighlightFamily[] = [
  'promise', 'comfort', 'command', 'conviction',
  'praise', 'character', 'plain',
];

// Which sub-label slugs belong to each family (drives the palette "More" view).
// Surviving slugs keep their original names even where the display string moved
// (e.g. `challenge` -> "Next Step") so a stored mark keeps rendering (spec §2).
export const HL_SUBLABELS: Record<HighlightFamily, string[]> = {
  promise: ['hope', 'healing'],
  comfort: ['peace', 'rest'],
  command: ['wisdom', 'challenge'],
  conviction: ['warning', 'turn-away'],
  praise: ['gratitude', 'love'],
  character: ['jesus', 'who-he-is'],
  plain: [],
};

// Theme-INDEPENDENT solid swatch/dot hex per family (spec §2). The single source
// of truth for the family-identity color, applied inline as a backgroundColor on
// the palette-sheet dots AND the journal-row dots (it is a fixed family-identity
// color, not reader text, so it sits outside the --reader-* token contract). The
// in-reader verse WASH is a separate per-theme --reader-hl-{key} token (rule
// #14): the dot is solid + identifiable on either sheet bg, the wash is
// translucent so body text keeps its contrast over it.
export const HL_DOT_HEX: Record<HighlightFamily, string> = {
  promise: '#e0a32e',
  comfort: '#5b9bd5',
  command: '#3aa88f',
  // "Careful": warm orange (was red). The freed wonder hue keeps the 6 families
  // well separated. Device-tunable; the orange mapping is locked (spec §2).
  conviction: '#e08544',
  praise: '#d36fae',
  character: '#8d7bc7',
  plain: '#9a9387',
};

// Rule #55 defensive coercion for orphaned device test data: after the union
// shrank, a stored `family: 'wonder'` (or a dropped sublabel) is out-of-union.
// This repo enables no strictNullChecks (tsconfig.app.json has no `strict`), so a
// `string` param can NOT reject `undefined` at compile time; the guard is RUNTIME.
const HL_FAMILY_SET = new Set<HighlightFamily>(HL_FAMILY_KEYS);

/** Nullish input (an unhighlighted verse) stays `undefined` (not highlighted); a
 *  PRESENT unknown string (e.g. a dropped 'wonder' test row) coerces to the
 *  nearest live sibling 'plain' (rule #55 defensive parity). */
export function coerceFamily(
  f: string | null | undefined,
): HighlightFamily | undefined {
  if (f == null) return undefined;
  return HL_FAMILY_SET.has(f as HighlightFamily) ? (f as HighlightFamily) : 'plain';
}

/** A sub-label no longer in its family's list is dropped (shown as the bare
 *  family name), so a removed slug never hits a missing i18n key. */
export function coerceSublabel(
  family: HighlightFamily,
  s: string | null | undefined,
): string | null {
  return s && HL_SUBLABELS[family].includes(s) ? s : null;
}
